import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "./utils";

const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md font-display text-sm font-semibold transition-colors disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 aria-invalid:border-destructive",
  {
    variants: {
      variant: {
        // TABS: Royal Indigo carries actions; hover deepens to Dark Indigo.
        default: "bg-royal-indigo text-white hover:bg-dark-indigo",
        // The one orange mark per view. Orange with white text needs 24px+ to pass contrast,
        // so the CTA size is part of the variant — don't shrink it with className.
        cta: "bg-vibrant-orange text-white text-2xl hover:bg-orange-on-light",
        destructive:
          "bg-danger-text text-white hover:bg-dark-indigo focus-visible:ring-destructive/20",
        outline:
          "border border-royal-indigo bg-white text-royal-indigo hover:bg-royal-indigo hover:text-white",
        // On Dark Indigo fields
        onDark: "bg-white text-dark-indigo hover:bg-indigo-tint-20",
        outlineOnDark:
          "border border-indigo-tint-50 bg-transparent text-white hover:bg-royal-indigo",
        secondary:
          "bg-indigo-tint-20 text-dark-indigo hover:bg-indigo-tint-50",
        ghost:
          "hover:bg-indigo-tint-20 hover:text-dark-indigo",
        link: "text-royal-indigo underline-offset-4 hover:underline",
      },
      size: {
        default: "h-9 px-4 py-2 has-[>svg]:px-3",
        sm: "h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5",
        lg: "h-11 rounded-md px-6 text-base has-[>svg]:px-4",
        cta: "h-14 rounded-md px-7 has-[>svg]:px-6",
        icon: "size-9 rounded-md",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };